import React, { useState } from "react";
import { convertDate, shouldFilterUsername } from "../../helpers/util_helper";
import { Link } from "react-router-dom";
import { Modal, ModalBody, Button } from "reactstrap";
import { Box, Typography, Divider } from "@mui/material";
import { Tooltip as MuiTooltip } from "@mui/material";

import UserDataGrid from "../DataGrid/UserDataGrid";
import getUserRole from "../../helpers/jwt-token-access/userRole";

const allowedRoles=[ "PowerUser", "Administrator" ];

export const MyDataGrid = ({
  usersConfig,
  setaccountList,
  setModalIsOpenToTrue,
  handleEditUser,
  roleAccess,
  setShowConfirmation,
  onToggle,
}) => {
    const userRole = getUserRole();
    const [toggleConfirmation, setToggleConfirmation] = useState(null);

    const handleToggleClick = (user) => {
        const action = user.status ? 'disable' : 'enable';
        const actionText = user.status ? 'Disable' : 'Enable';
        setToggleConfirmation({
            user,
            action,
            actionText,
            message: user.status 
              ? "This will prevent the user from accessing the system"
              : "This will allow the user to access the system"
        });
    };

    const handleToggleConfirm = () => {
        if (toggleConfirmation) {
            onToggle({
                email: toggleConfirmation.user.email,
                action: toggleConfirmation.action
            });
            setToggleConfirmation(null);
        }
    };

    // Custom tooltip content for status
    const StatusTooltip = ({ user }) => (
      <Box sx={{ p: 1, minWidth: 200 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
        >
          User Status
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: user.status ? "#28a745" : "#dc3545",
              mr: 1,
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {user.status ? "Active" : "Inactive"}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
          {user.status 
            ? "User can access the system and perform operations"
            : "User is locked and cannot access the system"
          }
        </Typography>
      </Box>
    );

    const usersTableDef = {
    columns: [
      {
        label: "",
        field: " ",
        sort: "disabled",
      },
      {
        label: "First Name",
        field: "firstname",
        sort: "asc",
      },
      {
        label: "Last Name",
        field: "lastName",
        sort: "asc",
      },
      {
        label: "User Name",
        field: "username",
        sort: "asc",
      },
      {
        label: "Email",
        field: "email",
        sort: "asc",
      },
      {
        label: "Phone Number",
        field: "phoneNumber",
        sort: "asc",
      },
      {
        label: "Country",
        field: "country",
        sort: "asc",
      },
      {
        label: "Accounts",
        field: "accounts",
        sort: "asc",
      },
      {
        label: "Role",
        field: "roles",
        sort: "asc",
      },
      {
        label: "Created Date",
        field: "createdDate",
        sort: "asc",
      },
      {
        label: "Resource Group",
        field: "resourceGroup",
        sort: "asc",
      },
      {
        label: "Status",
        field: "status",
        sort: "asc",
      },
      {
        label: "Action",
        field: "action",
        sort: "disabled",
      },
    ],
    rows:
      usersConfig &&
      usersConfig.filter(user => 
        !user.isDeleted && 
        !user.email.includes(process.env.REACT_APP_EMAIL_DOMAIN) &&
        !shouldFilterUsername(user.username)
      ).map((user, index) => {
        return {
          firstname: `${user.firstname}`,
          lastName: `${user.lastName}`,
          username: `${user.username}`,
          email: `${user.email}`,
          phoneNumber: `${user.phoneNumber}`,
          country: `${user.country}`,
          accounts: (
            <Link
              to="#"
              className="btn btn-link mr-3 text-darkblue"
              onClick={() => {
                setaccountList(
                  user.listacct.filter(
                    (x) => !x.accountNumber.includes("INIT00000001")
                  )
                );
                setModalIsOpenToTrue();
              }}
            >
              <div className="badge badge-pill badge-light">
                {/* INIT0000 */}
                {
                  user.listacct.filter(
                    (x) => !x.accountNumber.includes("INIT00000001")
                  ).length
                }
              </div>
            </Link>
          ),
          roles: `${user.roles}`,
          createdDate: `${convertDate(user.createdDate)}`,
          resourceGroup: (
            <div>
              {user.userGroup && user.userGroup.length > 0 ? (
                <MuiTooltip
                  title={user.userGroup.map((group) => (
                    <div>{group.resourcename}</div>
                  ))}
                >
                  <div className="badge badge-pill badge-light">
                    {user.userGroup.length}
                  </div>
                </MuiTooltip>
              ) : (
                "Unassigned"
              )}
            </div>
          ),
          status: (
            <div className="d-flex align-items-center">
              <MuiTooltip
                title={<StatusTooltip user={user} />}
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: 3,
                      "& .MuiTooltip-arrow": {
                        color: "background.paper",
                        "&::before": {
                          border: "1px solid",
                          borderColor: "divider",
                        },
                      },
                    },
                  },
                }}
              >
                <div 
                  className="mr-2" 
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: user.status ? '#28a745' : '#dc3545',
                    display: 'inline-block',
                    cursor: 'pointer'
                  }}
                ></div>
              </MuiTooltip>
              
              {/* Professional Toggle Button */}
              <div
                className={`d-flex align-items-center justify-content-center`}
                style={{
                  width: '32px',
                  height: '20px',
                  borderRadius: '10px',
                  backgroundColor: roleAccess < 0 
                    ? '#f5f5f5' 
                    : user.status 
                      ? '#28a745' 
                      : '#dc3545',
                  border: `1px solid ${
                    roleAccess < 0 
                      ? '#ddd' 
                      : user.status 
                        ? '#28a745' 
                        : '#dc3545'
                  }`,
                  cursor: roleAccess < 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  marginLeft: '8px',
                  position: 'relative'
                }}
                onClick={() => roleAccess >= 0 && handleToggleClick(user)}
                title={user.status ? "Disable User" : "Enable User"}
              >
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    position: 'absolute',
                    left: user.status ? '15px' : '1px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}
                ></div>
              </div>
            </div>
          ),
          action: (
            <div className="d-flex justify-content-between">
              <div>
                <button
                  className="btn btn-link text-darkblue btn-sm"
                  id={"edit" + index}
                  onClick={() => handleEditUser(user)}
                  disabled={
                    !allowedRoles.includes(userRole) || 
                    (userRole ==="PowerUser" ? `${user.roles}`==="Administrator" : false)
                  }
                  title="Modify User"
                >
                  <i className="mdi mdi-pencil font-size-18 text-darkblue"></i>
                </button>
              </div>
              <div>
                <button
                  className={`btn btn-link btn-sm`}
                  id={"delete" + index}
                  onClick={() =>
                    setShowConfirmation({ ...user, action: "delete" })
                  }
                  disabled={
                    !allowedRoles.includes(userRole) ||
                    (userRole ==="PowerUser" ? `${user.roles}`==="Administrator" : false)
                  }
                  title="Delete User"
                >
                  <i
                    className={`mdi mdi-trash-can font-size-18 ${
                      roleAccess < 0 
                        ? "text-secondary"
                        : "text-danger"
                    } `}
                  ></i>
                </button>
              </div>
              <div className="d-flex justify-content-around">
                <button
                  className="btn btn-link text-info btn-sm"
                  id={"reactivate" + index}
                  disabled={(user && user.status) || roleAccess < 0}
                  onClick={() =>
                    setShowConfirmation({ ...user, action: "reActive" })
                  }
                  title="Resend Activation Email"
                >
                  <i
                    className={`mdi mdi-refresh font-size-18 ${
                      user && user.status ? "text-secondary" : "text-danger"
                    }`}
                  ></i>
                </button>
              </div>
            </div>
          ),
        };
      }),
  };
  console.log("MyDataGrid is rendered");
  return (
    <div className="datatable">
      <UserDataGrid
        searchTop
        searchBottom={false}
        hover={true}
        responsive={true}
        small
        maxHeight="400px"
        entriesOptions={[10, 20, 30]}
        data={usersTableDef}
        fullPagination
      />
      
      {/* Toggle User Confirmation Modal */}
      <Modal isOpen={toggleConfirmation} className="col-sm-8">
        <ModalBody>
          <div className="d-flex flex-column align-items-center">
            <h4 className="text-secondary mb-3">
              {toggleConfirmation && `Are you sure you want to ${toggleConfirmation.actionText.toLowerCase()} ${toggleConfirmation.user.username}?`}
            </h4>
            <p className="text-muted text-center mb-3">
              {toggleConfirmation && toggleConfirmation.message}
            </p>
            <div className="mt-2 d-flex justify-content-end">
              <button
                className="btn btn-sm btn-secondary mr-2"
                onClick={() => setToggleConfirmation(null)}
              >
                Cancel
              </button>
              <button
                className={`btn btn-sm ${toggleConfirmation && toggleConfirmation.action === 'disable' ? 'btn-warning' : 'btn-success'} ml-2`}
                onClick={handleToggleConfirm}
              >
                {toggleConfirmation && toggleConfirmation.actionText}
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default MyDataGrid;